<template>
	<div
		class="card-product"
		v-loading="isLoading"
		@click="goTo('products')"
	>
		<div class="card-image">
			<img :src="product.urlImage" alt="">
			<div
				class="card-discount"
				ref="discount"
				:style="`background-color:${globalColors.hover};`"
				@mouseover="changeBG('primary')"
				@mouseout="changeBG('hover')"
			>
				{{ discountPercentage }}%
			</div>
		</div>
		<div class="card-content">
			<div class="content-title">
				<p :title="product.name">
					{{ getProductName }}
				</p>
				<p :title="product.description">
					{{ capitalizeDescription }}
				</p>
			</div>
			<p class="text-price" v-if="product.price">
				{{ getCurrencySymbol }}{{ product.price | priceFormat }}
			</p>
			<p
				class="text-discount"
				:style="`color:${globalColors.primary}`"
			>
				{{ getCurrencySymbol }}{{ getPriceDiscount | priceFormat }}
			</p>
		</div>
		<div class="wraper-is-not-stock" v-if="noStock">
			<span class="text-is-not">AGOTADO</span>
		</div>
	</div>
</template>

<script>
	import { mapGetters } from 'vuex';

	function capitalizeDescription() {
		const [first, ...rest] = this.product.description;
		if (first) {
			const newDescription = `${first.toUpperCase()}${rest.join('').toLowerCase()}`;
			const length = 36;
			if (newDescription.length > length) {
				const txt = newDescription.substring(0, length);
				return `${txt}...`;
			}
			return newDescription;
		}
		return '';
	}

	function discountPercentage() {
		const { price, priceDiscount } = this.product;
		if (price && priceDiscount) {
			return Number((((price - priceDiscount) / price) * 100).toFixed(2)) || 0;
		}
		return 0;
	}

	function getPriceDiscount() {
		return this.product.priceDiscount.toFixed(2) || 0;
	}

	function getProductName() {
		const { name } = this.product;
		const length = 30;
		if (name.length > length) {
			const txt = name.substring(0, length);
			return `${txt}...`;
		}
		return name;
	}

	function goTo(name) {
		this.$router.push({ name, params: { id: this.product.slug || this.product.id } });
	}

	function noStock() {
		const stock = !!this.product.stockWarehouse;
		const positiveStock = this.product.stockWarehouse > 0;
		return !(stock && positiveStock);
	}

	function changeBG(color) {
		this.$refs.discount.style.backgroundColor = this.globalColors[color];
	}

	export default {
		name: 'product-card',
		computed: {
			...mapGetters([
				'getCurrencySymbol',
			]),
			capitalizeDescription,
			discountPercentage,
			getProductName,
			getPriceDiscount,
			noStock,
		},
		methods: {
			changeBG,
			goTo,
		},
		props: {
			isLoading: {
				default: false,
				type: Boolean,
			},
			product: {
				default: () => {},
				type: Object,
			},
		},
	};
</script>

<style lang="scss" scoped>
	p {
		margin: 0px;
	}

	.card {
		&-product {
			cursor: pointer;
			display: flex;
			margin-bottom: 5.15rem;
			position: relative;
			transition: all .4s ease-in-out 0s;

			&:last-of-type {
				margin-bottom: 2.9rem;
			}

			@media (min-width: 768px) {
				align-items: center;
				flex: 1 1 auto;
				flex-basis: 33.33333333333333%;
				flex-direction: column;
				flex-grow: 0;
				justify-content: center;
				max-width: 33.33333333333333%;
				padding: 0px 1.2rem;
			}

			@media (min-width: 954px) {
				flex-basis: 25%;
				max-width: 25%;
			}

			&:hover {
				@media (min-width: 768px) {
					box-shadow: 0 0.2rem 0.2rem 0 rgba(31, 26, 26, 0.07);
				}
			}

			&:hover .no-stock-container {
				background-color: color(defaultBlack);
				filter: opacity(0.5);
			}

			&:hover .no-stock {
				display: block;
			}
		}

		&-discount {
			color: color(white);
			font-family: font(avenir);
			font-size: size(maxsmall);
			height: 2.58rem;
			left: 5.8rem;
			line-height: 2.9rem;
			position: absolute;
			text-align: center;
			top: -1.29rem;
			transition: all .4s ease-in-out 0s;
			width: 8.95rem;
			z-index: 1;

			@media (min-width: 768px) {
				bottom: 0rem;
				height: 3.24rem;
				left: auto;
				line-height: 3.56rem;
				right: -3rem;
				top: auto;
				width: 8.16rem;
			}
		}

		&-image {
			width: 13.6rem;

			img {
				height: 100%;
				object-fit: contain;
				position: relative;
				top: -1.7rem;
				width: 100%;

				@media (min-width: 768px) {
					position: static;
					top: auto;
				}
			}

			@media (min-width: 768px) {
				height: 17.5rem;
				position: relative;
			}
		}

		&-content {
			padding-top: 2rem;
			text-align: center;

			.content-title {
				color: color(titleGrey);
				font-family: font(avenir);
				font-size: size(maxsmall);
				line-height: 1.7rem;
				margin-bottom: 1.06rem;

				@media (min-width: 768px) {
					font-family: font(avenirDemi);
					font-size: size(medium);
					margin-top: 2.16rem;
					height: 6rem;
				}

				p {
					height: 3rem;
					width: 100%;
				}
			}

			.text-price {
				color: color(text-grey);
				font-family: font(avenir);
				font-size: size(maxsmall);
				text-decoration: line-through;

				@media (min-width: 768px) {
					font-size: size(msmall);
				}
			}

			.text-discount {
				font-family: font(avenirDemi);
				font-size: size(minlarge);

				@media (min-width: 768px) {
					font-size: size(maxsmall);
				}
			}

			@media (min-width: 768px) {
				max-width: 85%;
				padding-top: 0px;
			}
		}
	}

	.no-stock-container {
		background-color: transparent;
		height: 100%;
		left: 0;
		top: 0;
		width: 100%;
		z-index: 9;
	}

	.no-stock {
		background-color: color(defaultBlack);
		color: color(veryLightGrey);
		display: none;
		height: 4rem;
	}

	.wraper-is-not-stock {
		align-items: center;
		background-color: rgba(0, 0, 0, 0.62);
		color: color(white);
		display: flex;
		height: 4.3rem;
		justify-content: center;
		position: absolute;
		left: 1.2rem;
		right: 1.2rem;
		top: 31%;
		transform: translateY(-50%);
		z-index: 1;

		.text-is-not {
			font-family: font(avenirBold);
			font-size: size(maxsmall);
		}

		@media (max-width: 768px) {
			left: 0;
			right: 0;
			top: 50%;
		}
	}
</style>


// WEBPACK FOOTER //
// product-card.vue?06c68895